<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal" ref="licenseModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          {{ editMode ? $t('license_edit') : $t('license_added') }}
        </h2>
        <button class="modal-close-btn" @click="closeModal">
          <img class="cross" src="@/assets/images/cross.svg" alt="Close" />
        </button>
      </div>

      <div class="modal-body">
        <div class="license-form-wrapper">
          <div class="license-form-section">

            <div class="form-group" v-if="!editMode">
              <span class="form-label">{{ $t('packet_selected') }}:</span>

              <SelectBox
                  v-model="selectedPackageId"
                  :options="packageOptions"
                  :placeholder="$t('license_select') || 'Seçiniz'"
                  :full-width="true"
                  :loading="packagesLoading"
                  @update:modelValue="onPackageChange"
              />
            </div>

            <div class="calendar-wrapper">
              <div class="date-group">
                <span class="form-label">{{ $t('start_date') }}</span>
                <input
                    type="date"
                    class="form-input"
                    v-model="startDate"
                />
              </div>

              <div class="date-group">
                <span class="form-label">{{ $t('finish_date') }}</span>
                <input
                    type="date"
                    class="form-input"
                    v-model="endDate"
                />
              </div>
            </div>

            <div class="form-group">
              <span class="form-label">{{ $t('status') }}</span>
              <SelectBox
                  v-model="selectedStatus"
                  :options="statusOptions"
                  :placeholder="$t('select') || 'Seçiniz'"
                  :full-width="true"
              />
            </div>

            <div class="form-group">
              <span class="form-label">{{ $t('price') }}</span>
              <div class="price-inputs-wrapper">
                <SelectBox
                    v-model="selectedCurrency"
                    :options="currencyOptions"
                    :placeholder="$t('select_currency') || 'Para Birimi'"
                    :full-width="true"
                />

                <input
                    type="number"
                    class="form-input"
                    v-model="basePrice"
                    placeholder="0.00"
                    :min="0"
                    step="0.01"
                />
              </div>
            </div>

            <div class="form-group">
              <span class="form-label">{{ $t('license_assignment_type') }}</span>
              <div class="assignment-type-radio">
                <label class="radio-option">
                  <input type="radio" v-model="assignmentType" value="free" />
                  <span>{{ $t('license_assignment_free') }}</span>
                </label>
                <label class="radio-option">
                  <input type="radio" v-model="assignmentType" value="pending" />
                  <span>{{ $t('license_assignment_pending') }}</span>
                </label>
              </div>
            </div>

            <div class="form-group">
              <span class="form-label">{{ $t('notes') }}</span>
              <textarea
                  class="form-textarea"
                  :placeholder="this.$t('notes_enter')"
                  v-model="notes"
              ></textarea>
            </div>
          </div>

          <div class="license-products-section">
            <h3 class="products-title">{{ $t('products') }}</h3>

            <div v-if="!selectedPackage" class="no-package-selected">
              {{ $t('please_select_package') }}
            </div>

            <div v-else class="product-list">
              <div class="product-item" v-for="(product, index) in products" :key="index">
                <div class="product-info">
                  <span class="product-name">{{ product.name }}</span>
                  <span class="product-unit" v-if="product.limitType !== 'isCheck'">{{ getTranslatedUnit(product.unit) }}</span>
                </div>

                <div v-if="product.limitType === 'isCheck'" class="product-checkbox-wrapper">
                  <input
                      type="checkbox"
                      class="product-checkbox"
                      v-model="product.isEnabled"
                      :id="`product-${index}`"
                  />
                  <label :for="`product-${index}`" class="checkbox-label"></label>
                </div>

                <input
                    v-else
                    type="number"
                    class="product-quantity-input"
                    v-model="product.quantity"
                    :min="0"
                />
              </div>
            </div>

            <div class="footer-note">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7 13.25C10.4518 13.25 13.25 10.4518 13.25 7C13.25 3.54822 10.4518 0.75 7 0.75C3.54822 0.75 0.75 3.54822 0.75 7C0.75 10.4518 3.54822 13.25 7 13.25ZM8.11584 10.2086L8.21565 9.8006C8.16399 9.82486 8.0807 9.85258 7.96649 9.88414C7.85197 9.9157 7.7489 9.93177 7.65831 9.93177C7.46536 9.93177 7.32952 9.90014 7.25065 9.83657C7.17236 9.773 7.13331 9.65342 7.13331 9.47827C7.13331 9.40887 7.14509 9.30542 7.16973 9.17003C7.19361 9.03373 7.22108 8.91261 7.25161 8.80666L7.62419 7.48759C7.66068 7.36653 7.68571 7.23344 7.69916 7.08818C7.71292 6.94325 7.71932 6.84185 7.71932 6.78429C7.71932 6.50614 7.62182 6.28041 7.42676 6.10629C7.2317 5.93229 6.95393 5.84529 6.59396 5.84529C6.39365 5.84529 6.18188 5.88088 5.95776 5.952C5.73363 6.02294 5.49933 6.1084 5.25421 6.2082L5.15415 6.6165C5.22719 6.58949 5.31419 6.56043 5.41598 6.53034C5.51732 6.50038 5.61674 6.48489 5.71347 6.48489C5.91096 6.48489 6.04399 6.51856 6.1137 6.58488C6.18342 6.65139 6.21844 6.7697 6.21844 6.93883C6.21844 7.03236 6.20736 7.13626 6.18438 7.24919C6.16172 7.36282 6.13342 7.48298 6.10013 7.6098L5.72595 8.93419C5.69266 9.07336 5.66834 9.19787 5.65304 9.30843C5.63786 9.41886 5.63057 9.52724 5.63057 9.63262C5.63057 9.90482 5.73114 10.1292 5.93222 10.3063C6.13329 10.4826 6.41523 10.5714 6.77769 10.5714C7.01372 10.5714 7.22088 10.5406 7.39917 10.4785C7.57727 10.4167 7.81644 10.3268 8.11584 10.2086ZM8.04946 4.8502C8.22352 4.68882 8.31014 4.49254 8.31014 4.26272C8.31014 4.03341 8.22365 3.83675 8.04946 3.67331C7.87584 3.51032 7.66657 3.42857 7.4219 3.42857C7.17646 3.42857 6.96635 3.51013 6.79107 3.67331C6.61579 3.83675 6.52796 4.03334 6.52796 4.26272C6.52796 4.49254 6.61579 4.68875 6.79107 4.8502C6.96667 5.01217 7.17639 5.09321 7.4219 5.09321C7.66664 5.09321 7.87584 5.01217 8.04946 4.8502Z" fill="#98A2B3"/>
              </svg>
              {{ $t('you_adjust_product_price_limits') }}
            </div>
          </div>

        </div>
      </div>

      <div class="modal-footer">
        <button class="dark-button-outline" @click="closeModal">
          {{ $t('cancel') }}
        </button>
        <button class="primary-button" @click="saveLicense" :disabled="!isFormValid || isLoading">
          {{ isLoading ? $t('Kaydediliyor...') : $t('save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import pricingService from "@/service/pricingService";
import companyService from "@/service/companyService";
import moment from "moment";
import SelectBox from "@/components/SelectBox.vue";

export default {
  name: 'LicenseModal',
  components: { SelectBox },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function,
      required: true
    },
    refreshCallback: {
      type: Function,
      default: () => {}
    },
    companyId: {
      type: [String, Number],
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      companyPackageId: null,
      selectedPackage: null,
      selectedPackageId: null,
      selectedStatus: '',
      selectedCurrency: 'USD',
      startDate: '',
      endDate: '',
      notes: '',
      basePrice: '',
      isLoading: false,
      availablePackages: [],
      packagesLoading: false,
      packageDetailsLoading: false,
      packageDetailsLoaded: false,
      products: [],
      // 'free' (hediye, default) | 'pending' (ödeme bekleniyor)
      assignmentType: 'free',
    }
  },

  computed: {
    isFormValid() {
      return this.selectedPackage &&
          this.selectedStatus &&
          this.startDate &&
          this.endDate;
    },

    packageOptions() {
      return this.availablePackages.map(pkg => {
        let displayText = pkg.name;
        let disabled = false;

        // canAdd false ise sebebe göre mesaj ekle
        if (pkg.canAdd === false) {
          disabled = true;
          switch (pkg.reason) {
            case 'demo_already_used':
              displayText = `${pkg.name} (${this.$t('demo_already_used')})`;
              break;
            case 'paid_package_used':
              displayText = `${pkg.name} (${this.$t('cannot_return_to_demo')})`;
              break;
            case 'package_already_active':
              displayText = `${pkg.name} (${this.$t('package_already_active')})`;
              break;
            case 'downgrade_limit_exceeded':
              displayText = `${pkg.name} (${this.$t('downgrade_limit_exceeded')})`;
              break;
            default:
              displayText = `${pkg.name} (${this.$t('not_available')})`;
          }
        }

        // Mevcut paket varsa ve aktif değilse bilgi ekle
        if (pkg.existingStatus && pkg.existingStatus !== 'active' && !disabled) {
          const statusText = this.$t(pkg.existingStatus);
          displayText = `${pkg.name} (${statusText})`;
        }

        return {
          value: pkg.packageId,
          text: displayText,
          disabled: disabled
        };
      });
    },

    statusOptions() {
      return [
        { value: 'active', text: this.$t('active') },
        { value: 'suspended', text: this.$t('suspended') },
        { value: 'cancelled', text: this.$t('cancelled') }
      ];
    },

    currencyOptions() {
      return [
        { value: 'USD', text: this.$t('currency_usd') },
        { value: 'EUR', text: this.$t('currency_eur') },
        { value: 'TRY', text: this.$t('currency_try') }
      ];
    }
  },

  watch: {
    isVisible(newValue) {
      if (newValue) {
        this.loadPackageList();

        if (this.editMode && this.editData && Object.keys(this.editData).length > 0) {
          setTimeout(() => {
            if (!this.packageDetailsLoaded) {
              this.fillFormWithEditData(this.editData);
            }
          }, 500);
        }
      } else {
        this.resetForm();
      }
    },

    editMode(newValue) {
      if (!newValue) {
        this.selectedPackage = null;
        this.selectedPackageId = null;
      }
    },

    availablePackages: {
      handler(newPackages) {
        if (newPackages.length > 0 && this.editMode && this.editData && Object.keys(this.editData).length > 0) {
          this.$nextTick(() => {
            if (!this.packageDetailsLoaded) {
              this.fillFormWithEditData(this.editData);
            }
          });
        }
      }
    },

    selectedPackage: {
      handler(newPackage) {
        if (newPackage && this.companyId) {
          this.loadPackageDetails(
              newPackage.packageId,
              this.companyId,
              this.editMode ? this.companyPackageId : null
          );
        }
      },
      deep: true
    },

    companyId(newCompanyId) {
      if (newCompanyId && this.selectedPackage) {
        this.loadPackageDetails(
            this.selectedPackage.packageId,
            newCompanyId,
            this.editMode ? this.companyPackageId : null
        );
      }
    }
  },

  methods: {
    closeModal() {
      this.onClose();
      this.resetForm();
    },

    resetForm() {
      this.companyPackageId = null;
      this.selectedPackage = null;
      this.selectedPackageId = null;
      this.selectedStatus = '';
      this.selectedCurrency = 'USD';
      this.startDate = '';
      this.endDate = '';
      this.notes = '';
      this.basePrice = '';
      this.isLoading = false;
      this.packageDetailsLoading = false;
      this.packageDetailsLoaded = false;
      this.products = [];
    },

    async loadPackageList() {
      if (this.packagesLoading) return;

      this.packagesLoading = true;

      try {
        // companyId ile çağır - uygunluk bilgisi için
        const response = await pricingService.getPackageList(this.companyId);

        if (response.data.responseType === 'success') {
          this.availablePackages = response.data.data || [];
        } else {
          this.$snackbar.add({
            type: 'error',
            text: this.$t('package_list_load_error')
          });
        }
      } catch (error) {
        this.$snackbar.add({
          type: 'error',
          text: this.$t('package_list_load_error')
        });
      } finally {
        this.packagesLoading = false;
      }
    },

    async loadPackageDetails(packageId, companyId, companyPackageId = null) {
      if (this.packageDetailsLoading) return;

      if (!companyId) {
        return;
      }

      this.packageDetailsLoading = true;

      try {
        const response = await pricingService.getPackageDetails(
            packageId,
            companyId,
            companyPackageId
        );

        if (response.data.responseType === 'success') {
          const packageDetails = response.data.data;

          if (this.editMode) {
            this.fillFormWithEditData(packageDetails);
            this.packageDetailsLoaded = true;
          }

          // ✅ basePrice ve currency backend'den geliyorsa kullan
          if (packageDetails.basePrice) {
            this.basePrice = packageDetails.basePrice;
          }

          if (packageDetails.currency) {
            this.selectedCurrency = packageDetails.currency;
          }

          this.products = pricingService.extractProductsFromPackage(packageDetails);
        } else {
          this.$snackbar.add({
            type: 'warning',
            text: this.$t('package_details_load_warning')
          });
        }
      } catch (error) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('package_details_load_warning')
        });
      } finally {
        this.packageDetailsLoading = false;
      }
    },

    onPackageChange(packageId) {
      const packageItem = this.availablePackages.find(pkg => pkg.packageId === packageId);
      if (packageItem) {
        this.selectedPackage = packageItem;
        this.selectedPackageId = packageId;
      }
    },

    fillFormWithEditData(data) {
      this.companyPackageId = data.companyPackageId || null;

      if (data.packageName && this.availablePackages.length > 0) {
        const foundPackage = this.availablePackages.find(
            pkg => pkg.name === data.packageName
        );
        if (foundPackage) {
          this.selectedPackage = foundPackage;
          this.selectedPackageId = foundPackage.packageId;
        }
      } else if (data.packageId) {
        const foundPackage = this.availablePackages.find(
            pkg => pkg.packageId === data.packageId
        );
        if (foundPackage) {
          this.selectedPackage = foundPackage;
          this.selectedPackageId = foundPackage.packageId;
        }
      }

      this.selectedStatus = data.status || '';
      this.selectedCurrency = data.currency || 'USD';
      this.startDate = this.formatDateForInput(data.startDate) || '';
      this.endDate = this.formatDateForInput(data.endDate) || '';
      this.basePrice = data.basePrice || '';
      this.notes = data.notes || '';
    },

    formatDateForInput(dateStr) {
      try {
        if (!dateStr) return '';
        const date = new moment(dateStr, 'DD-MM-YYYY');
        return date.isValid() ? date.format('YYYY-MM-DD') : '';
      } catch (e) {
        return '';
      }
    },

    formatDateForBackend(dateStr) {
      try {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
      } catch (e) {
        return '';
      }
    },

    getStatusForBackend(status) {
      const statusMap = {
        'active': 'active',
        'suspended': 'suspended',
        'cancelled': 'cancelled'
      };
      return statusMap[status] || 'active';
    },

    getTranslatedUnit(unit) {
      const unitMap = {
        'Adet': 'unit_count',
        'Dakika': 'unit_minute',
        'Saat': 'unit_hour',
        'GB': 'unit_gb',
        'MB': 'unit_mb',
        'TB': 'unit_tb'
      };
      return this.$t(unitMap[unit] || unit);
    },

    async saveLicense() {
      if (!this.isFormValid) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('required_fields_warning')
        });
        return;
      }

      this.isLoading = true;

      try {
        const formattedProducts = this.products.map(product => ({
          companyPackageProductId: product.companyPackageProductId || null,
          packageProductId: product.packageProductId || null,
          productId: product.productId,
          productKey: product.productKey,
          name: product.name,
          basePrice: product.basePrice || 0,
          isChargeable: product.isChargeable || true,
          limitValue: product.limitType === 'isCheck'
              ? (product.isEnabled ? "1" : "0")
              : String(product.quantity || 0),
          limitType: product.limitType
        }));

        const licenseData = {
          companyPackageId: this.companyPackageId || null,
          companyId: this.companyId,
          packageId: this.selectedPackage.packageId,
          startDate: this.formatDateForBackend(this.startDate),
          endDate: this.formatDateForBackend(this.endDate),
          status: this.getStatusForBackend(this.selectedStatus),
          basePrice: this.basePrice || "0",
          currency: this.selectedCurrency,
          notes: this.notes || null,
          products: formattedProducts,
          // 'free' = hediye, 'pending' = ödeme bekleniyor
          assignmentType: this.assignmentType,
        };

        await companyService.addCompanyLicense(licenseData);

        this.$snackbar.add({
          type: 'success',
          text: this.companyPackageId
              ? this.$t('license_updated_successfully')
              : this.$t('license_created_successfully')
        });

        this.closeModal();
        this.refreshCallback();

      } catch (error) {
        if (error.response?.status === 422) {
          this.$snackbar.add({
            type: 'warning',
            text: error.response.data.message || this.$t('Bu paket zaten şirkete atanmış durumda')
          });
        } else {
          this.$snackbar.add({
            type: 'error',
            text: this.editMode
                ? this.$t('license_update_error')
                : this.$t('license_create_error')
          });
        }
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style scoped>
/* Tüm styles aynı kalacak - sadece gereksiz dropdown-menu kısımlarını kaldırdım */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #E5E7EB;
}

.modal-title {
  font-family: euclid_medium, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modal-close-btn {
  display: flex;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  background-color: #F3F4F6;
  height: 32px;
  width: 32px;
  justify-content: center;
  align-items: center;
}
.cross{
  height: 12px;
  width: 12px;
}

.modal-body {
  padding: 30px;
}

.license-form-wrapper {
  display: flex;
  flex-direction: row;
  gap: 30px;
}

.license-form-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
  width: 50%;
}

.license-products-section {
  display: flex;
  flex-direction: column;
  width: 50%;
}

.no-package-selected {
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
  font-family: euclid-regular, sans-serif;
  font-size: 14px;
  color: #6B7280;
}

.calendar-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
}

.date-group {
  display: flex;
  flex-direction: column;
}

.form-input {
  font-size: 14px;
  color: #374151;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  border: none;
  width: 100%;
  font-family: euclid_regular, sans-serif;
  height: 50px;
}

.form-input:focus {
  outline: none;
  border: 1px solid #1D4ED8;
}

.form-textarea {
  width: 100%;
  min-height: 120px;
  padding: 15px;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  font-family: euclid_medium, sans-serif;
  resize: vertical;
}

.form-textarea:focus {
  outline: none;
  border-color: #1D4ED8;
}

.products-title {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  padding-bottom: 10px;
}

.product-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-name {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.product-unit {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #6366F1;
}

.product-quantity-input {
  width: 80px;
  height: 35px;
  padding: 0 10px;
  background-color: #ffffff;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  text-align: center;
}

.product-checkbox-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.product-checkbox {
  display: none;
}

.checkbox-label {
  position: relative;
  width: 20px;
  height: 20px;
  background-color: #ffffff;
  border: 2px solid #D1D5DB;
  border-radius: 4px;
  cursor: pointer;
}

.product-checkbox:checked + .checkbox-label {
  background-color: #1D4ED8;
  border-color: #1D4ED8;
}

.product-checkbox:checked + .checkbox-label:after {
  content: '';
  position: absolute;
  top: 2px;
  left: 6px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.footer-note {
  margin-top: 20px;
  padding-top: 15px;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #6B7280;
  display: flex;
  gap: 5px;
  align-items: center;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 30px;
  border-top: 1px solid #E5E7EB;
}

.primary-button:disabled {
  background-color: #9CA3AF;
  cursor: not-allowed;
}

.price-inputs-wrapper {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 12px;
  width: 100%;
}

.form-input-readonly {
  width: 100%;
  height: 45px;
  padding: 0 15px;
  background-color: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  display: flex;
  align-items: center;
  cursor: not-allowed;
}

.form-input-readonly span {
  color: #6B7280;
  font-size: 14px;
  font-family: euclid_medium, sans-serif;
}

@media (max-width: 768px) {
  .license-form-wrapper {
    flex-direction: column;
  }

  .license-form-section,
  .license-products-section {
    width: 100%;
  }

  .calendar-wrapper,
  .price-inputs-wrapper {
    grid-template-columns: 1fr;
  }
}

.assignment-type-radio {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.assignment-type-radio .radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-family: euclid_regular, sans-serif;
  color: #344054;
}
.assignment-type-radio input[type="radio"] {
  cursor: pointer;
}
</style>
